import { ApiError, TenantContext } from '../types';

export class ApiClient {
  private baseURL: string;
  private tenantContext: TenantContext | null = null;
  private isRefreshing = false;
  private refreshPromise: Promise<string> | null = null;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  setTenantContext(context: TenantContext): void {
    this.tenantContext = context;
  }

  private async refreshAccessToken(): Promise<string> {
    if (this.isRefreshing) {
      return this.refreshPromise!;
    }

    this.isRefreshing = true;
    this.refreshPromise = this.performTokenRefresh();

    try {
      const newToken = await this.refreshPromise;
      return newToken;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  // Đọc claim `exp` từ JWT (không verify chữ ký — chỉ dùng để quyết định có nên refresh chủ động
  // trước khi gửi request hay không, verify thật vẫn do backend làm). Token hỏng/không decode được
  // thì coi như sắp hết hạn, để luồng 401 phản ứng (reactive) phía dưới xử lý như trước.
  private isTokenExpiringSoon(token: string, bufferSeconds = 30): boolean {
    try {
      const payloadB64 = token.split('.')[1];
      const payload = JSON.parse(atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/')));
      if (typeof payload.exp !== 'number') return false;
      return payload.exp * 1000 < Date.now() + bufferSeconds * 1000;
    } catch {
      return true;
    }
  }

  private async performTokenRefresh(): Promise<string> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await fetch(`${this.baseURL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refresh_token: refreshToken,
      }),
    });

    if (!response.ok) {
      // Refresh failed, clear tokens and force re-login
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user_profile');
      throw new Error('Token refresh failed');
    }

    const data = await response.json();
    
    // Store new tokens
    localStorage.setItem('auth_token', data.access_token);
    localStorage.setItem('refresh_token', data.refresh_token);
    
    return data.access_token;
  }

  private async request<T>(
    endpoint: string, 
    options: RequestInit = {},
    isRetry = false
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    // For FormData bodies, let the browser set Content-Type (with multipart boundary).
    const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
    const headers: Record<string, string> = {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(options.headers as Record<string, string> || {}),
    };

    // Add tenant-specific headers
    if (this.tenantContext?.organization_slug) {
      headers['X-Organization-Slug'] = this.tenantContext.organization_slug;
    }

    // Add authentication token — refresh chủ động nếu token sắp hết hạn (thay vì đợi request đầu
    // tiên rớt 401 rồi mới refresh và gọi lại như trước), tránh request đầu luôn bị round-trip 401
    // thừa. Không áp dụng cho chính /auth/refresh và /auth/login để tránh đệ quy.
    let token = localStorage.getItem('auth_token');
    if (token && !isRetry && endpoint !== '/auth/refresh' && endpoint !== '/auth/login' && this.isTokenExpiringSoon(token)) {
      try {
        token = await this.refreshAccessToken();
      } catch {
        // Refresh chủ động thất bại — vẫn gửi request với token cũ, để luồng 401 phía dưới xử lý
        // logout/redirect thống nhất một chỗ thay vì lặp lại logic ở đây.
      }
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Add CSRF token if available
    const csrfToken = document.querySelector<HTMLMetaElement>(
      'meta[name="csrf-token"]'
    )?.content;
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include', // Include cookies for CSRF token
    });

    // Handle 401 Unauthorized - attempt token refresh
    if (response.status === 401 && !isRetry && endpoint !== '/auth/refresh' && endpoint !== '/auth/login') {
      try {
        const newToken = await this.refreshAccessToken();
        // Retry the original request with the new token
        const newHeaders = {
          ...headers,
          'Authorization': `Bearer ${newToken}`,
        };
        
        return this.request<T>(endpoint, { ...options, headers: newHeaders }, true);
      } catch (refreshError) {
        // Refresh failed, redirect to login or handle accordingly
        console.error('Token refresh failed:', refreshError);
        // Clear tokens and throw the original 401 error
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user_profile');
        
        // Trigger logout event
        window.dispatchEvent(new CustomEvent('auth:logout'));
        
        throw new ApiError(401, 'Authentication failed');
      }
    }

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = errorText;
      let errorCode: string | undefined;

      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorData.detail || errorData.error || errorText;
        errorCode = errorData.error_code;
      } catch {
        // Keep original error text if not JSON
      }

      // Chỉ bail impersonation khi ĐÚNG lý do "phiên mất quyền truy cập tổ chức"
      // (error_code 'org_mismatch', do RequireOrgMatch phát ra) — KHÔNG phải mọi
      // 403. Trước đây coi MỌI 403 lúc impersonate là dấu hiệu phiên hỏng, nên 403
      // do thiếu quyền cho 1 thao tác cụ thể (vd RequireOrgAdmin — 'insufficient_role',
      // hoàn toàn bình thường khi impersonate 1 user thấp quyền hơn org_admin) cũng bị
      // tự thoát impersonation + văng về /admin/users oan — bug thật lộ ra khi test nút
      // Export YAML (chặn bằng RequireOrgAdmin) trong lúc đang "Login As".
      if (response.status === 403 && errorCode === 'org_mismatch' && localStorage.getItem('admin_token')) {
        window.dispatchEvent(new CustomEvent('auth:impersonation-forbidden'));
      }

      // Task 070-E: 403 hiện câu tiếng Việt dễ hiểu thay vì JSON thô / tiếng Anh của server.
      if (response.status === 403 && errorCode) {
        const friendly: Record<string, string> = {
          insufficient_scope: 'Tài khoản quản lý outlet không có quyền dùng chức năng này.',
          insufficient_role: 'Chức năng này chỉ dành cho quản trị tổ chức.',
          org_mismatch: 'Bạn không có quyền truy cập tổ chức này.',
        };
        if (friendly[errorCode]) errorMessage = friendly[errorCode]; // mã khác (vd adjust_limit_exceeded) giữ nguyên message để UI tự xử lý
      }

      throw new ApiError(response.status, errorMessage);
    }

    // Handle empty responses
    const contentType = response.headers.get('content-type');
    if (!contentType?.includes('application/json')) {
      return {} as T;
    }

    return response.json();
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T, U = unknown>(endpoint: string, data: U): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put<T, U = unknown>(endpoint: string, data: U): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async patch<T, U = unknown>(endpoint: string, data: U): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  async upload<T>(endpoint: string, formData: FormData): Promise<T> {
    return this.request<T>(endpoint, { method: 'POST', body: formData });
  }
}

// Create singleton instance
export const apiClient = new ApiClient(
  import.meta.env.VITE_API_BASE_URL || 'http://localhost:3011/api/v1'
);